import {
   Badge,
   Container,
   Group,
   Loader,
   Pagination,
   Paper,
   Stack,
   Text,
   ThemeIcon,
   Title,
   TextInput,
   Menu,
   ActionIcon,
   Tabs,
   Table,
   ScrollArea,
   Modal,
   Button,
   Divider,
   Grid,
   Box,
   Image,
   Tooltip,
   SimpleGrid,
} from "@mantine/core";
import {
   IconFileCheck,
   IconCheck,
   IconX,
   IconSearch,
   IconDots,
   IconEye,
   IconClock,
   IconUser,
   IconPhone,
   IconMail,
   IconCalendar,
   IconFileText,
   IconShieldCheck,
   IconBriefcase,
   IconHome,
   IconCurrencyDollar,
   IconDownload,
} from "@tabler/icons-react";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { usePerformRequest, PerformRequest } from "../../lib/usePerformRequest";
import { IPaginationProps, PaginatedResponse } from "../../types";
import { IKycProps, IKycResponseProps } from "../../types/Kyc";
import { Endpoints } from "../../lib/endpoints";
import { useToastNotification } from "../../hooks/useToastNotification";
import { HttpCodes } from "../../lib/data";

export default function AdminKYC() {
   const DOCUMENT_LABELS: Record<"identity" | "address" | "employment" | "financial" | "nextOfKin", string> = {
      identity: "Identity Document",
      address: "Proof of Address",
      employment: "Proof of Employment",
      financial: "Financial Document",
      nextOfKin: "Next of Kin",
   };
   const [searchQuery, setSearchQuery] = useState("");
   const [activeTab, setActiveTab] = useState<string | null>("all");
   const [pagination, setPagination] = useState<IPaginationProps>({
      page: 1,
      limit: 10,
   });
   const [updatingKycId, setUpdatingKycId] = useState<string | null>(null);
   const [selectedKyc, setSelectedKyc] = useState<IKycResponseProps | null>(null);
   const [modalOpened, setModalOpened] = useState(false);
   const [updatingField, setUpdatingField] = useState<string | null>(null);
   const [confirmation, setConfirmation] = useState<{
      userID: string;
      field: "identity" | "address" | "employment" | "financial" | "nextOfKin";
      status: "approved" | "rejected";
   } | null>(null);
   const { success, error } = useToastNotification();

   const {
      data: kycResponse,
      isLoading,
      reloadData: reloadKYC,
   } = usePerformRequest<PaginatedResponse<IKycResponseProps[]>>({
      method: Endpoints.GetKYCRequests(pagination.page, pagination.limit).method,
      route: Endpoints.GetKYCRequests(pagination.page, pagination.limit).route,
   });

   useEffect(() => {
      reloadKYC();
   }, [pagination]);

   // Sync selectedKyc with updated data from kycResponse when it reloads
   useEffect(() => {
      if (selectedKyc && kycResponse?.data) {
         const updatedKyc = kycResponse.data.find((k) => k.userID.id === selectedKyc.userID.id);
         if (updatedKyc) {
            setSelectedKyc(updatedKyc);
         }
      }
   }, [kycResponse?.data]);

   const formatDate = (timestamp: number) => {
      return new Date(timestamp).toLocaleDateString("en-US", {
         year: "numeric",
         month: "short",
         day: "numeric",
         hour: "2-digit",
         minute: "2-digit",
      });
   };

   const getKycStatus = (kyc: IKycResponseProps): "verified" | "pending" | "partial" => {
      // Check if all 5 documents are approved
      const allApproved =
         kyc.identityStatus === "approved" &&
         kyc.employmentStatus === "approved" &&
         kyc.addressStatus === "approved" &&
         kyc.financialDocumentStatus === "approved" &&
         kyc.nextOfKinStatus === "approved";

      if (allApproved) return "verified";

      // Check if any document is pending or approved
      const hasPendingOrApproved =
         kyc.identityStatus === "pending" ||
         kyc.identityStatus === "approved" ||
         kyc.employmentStatus === "pending" ||
         kyc.employmentStatus === "approved" ||
         kyc.addressStatus === "pending" ||
         kyc.addressStatus === "approved" ||
         kyc.financialDocumentStatus === "pending" ||
         kyc.financialDocumentStatus === "approved" ||
         kyc.nextOfKinStatus === "pending" ||
         kyc.nextOfKinStatus === "approved";

      if (hasPendingOrApproved) return "partial";
      return "pending";
   };

   const getStatusColor = (status: "verified" | "pending" | "partial") => {
      switch (status) {
         case "verified":
            return "green";
         case "partial":
            return "blue";
         default:
            return "yellow";
      }
   };

   const getStatusIcon = (status: "verified" | "pending" | "partial") => {
      switch (status) {
         case "verified":
            return IconCheck;
         case "partial":
            return IconClock;
         default:
            return IconX;
      }
   };

   const getDocumentStatusIcon = (status?: "awaiting" | "pending" | "approved" | "rejected") => {
      if (!status || status === "awaiting") {
         return { icon: IconX, color: "red", label: "Awaiting" };
      }
      if (status === "approved") {
         return { icon: IconCheck, color: "green", label: "Approved" };
      }
      if (status === "pending") {
         return { icon: IconClock, color: "yellow", label: "Pending" };
      }
      if (status === "rejected") {
         return { icon: IconX, color: "red", label: "Rejected" };
      }
      return { icon: IconX, color: "red", label: "Awaiting" };
   };

   const getDocumentStatus = (
      kyc: IKycResponseProps,
      field: "identity" | "address" | "employment" | "financial" | "nextOfKin"
   ): "awaiting" | "pending" | "approved" | "rejected" => {
      switch (field) {
         case "identity":
            return kyc.identityStatus || "awaiting";
         case "address":
            return kyc.addressStatus || "awaiting";
         case "employment":
            return kyc.employmentStatus || "awaiting";
         case "financial":
            return kyc.financialDocumentStatus || "awaiting";
         case "nextOfKin":
            return kyc.nextOfKinStatus || "awaiting";
         default:
            return "awaiting";
      }
   };

   const hasDocument = (
      kyc: IKycResponseProps,
      field: "identity" | "address" | "employment" | "financial" | "nextOfKin"
   ): boolean => {
      switch (field) {
         case "identity":
            return Boolean(kyc.identity);
         case "address":
            return Boolean(kyc.proofOfAddress);
         case "employment":
            return Boolean(kyc.proofOfEmployment);
         case "financial":
            return Boolean(kyc.financialDocument);
         case "nextOfKin":
            return Boolean(kyc.nextOfKin);
         default:
            return false;
      }
   };

   const totalPages = kycResponse ? Math.ceil(kycResponse.totalRecords / kycResponse.limit) : 1;

   // Calculate stats from actual data
   const stats = {
      total: kycResponse?.totalRecords || 0,
      pending:
         kycResponse?.data?.filter((kyc) => {
            const status = getKycStatus(kyc);
            return status === "pending";
         }).length || 0,
      verified:
         kycResponse?.data?.filter((kyc) => {
            const status = getKycStatus(kyc);
            return status === "verified";
         }).length || 0,
      partial:
         kycResponse?.data?.filter((kyc) => {
            const status = getKycStatus(kyc);
            return status === "partial";
         }).length || 0,
   };

   const handleViewDetails = (kyc: IKycResponseProps) => {
      setSelectedKyc(kyc);
      setModalOpened(true);
   };

   const applyLocalStatusUpdate = (
      userID: string,
      field: "identity" | "address" | "employment" | "financial" | "nextOfKin",
      status: "approved" | "rejected"
   ) => {
      setSelectedKyc((prev) => {
         if (!prev || prev.userID.id !== userID) {
            return prev;
         }

         const updated: IKycResponseProps = {
            ...prev,
         };

         const assignStatus = (
            statusKey: keyof IKycResponseProps,
            verifiedKey: keyof IKycResponseProps,
            submittedKey?: keyof IKycResponseProps
         ) => {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore - dynamic assignment is safe here because keys are controlled
            updated[statusKey] = status;
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            updated[verifiedKey] = status === "approved";
            if (submittedKey) {
               // eslint-disable-next-line @typescript-eslint/ban-ts-comment
               // @ts-ignore
               updated[submittedKey] = true;
            }
         };

         switch (field) {
            case "identity":
               assignStatus("identityStatus", "isIdentityVerified", "isIdentitySubmitted");
               break;
            case "address":
               assignStatus("addressStatus", "isAddressVerified", "isAddressSubmitted");
               break;
            case "employment":
               assignStatus("employmentStatus", "isEmploymentVerified", "isEmploymentSubmitted");
               break;
            case "financial":
               assignStatus("financialDocumentStatus", "isFinancialDocumentVerified", "isFinancialDocumentSubmitted");
               break;
            case "nextOfKin":
               assignStatus("nextOfKinStatus", "isNextOfKinVerified", "isNextOfKinSubmitted");
               break;
         }

         const allApproved =
            updated.identityStatus === "approved" &&
            updated.addressStatus === "approved" &&
            updated.employmentStatus === "approved" &&
            updated.financialDocumentStatus === "approved" &&
            updated.nextOfKinStatus === "approved";

         updated.isVerified = allApproved;

         return updated;
      });
   };

   const handleUpdateFieldVerification = async (
      userID: string,
      field: "identity" | "address" | "employment" | "financial" | "nextOfKin",
      status: "approved" | "rejected"
   ) => {
      setUpdatingField(field);

      // Optimistically update the UI immediately - this will show the status badge right away
      applyLocalStatusUpdate(userID, field, status);

      try {
         // Map frontend field names to API field names
         const apiFieldMap: Record<string, "identity" | "employment" | "address" | "financialDocument" | "nextOfKin"> =
            {
               identity: "identity",
               address: "address",
               employment: "employment",
               financial: "financialDocument",
               nextOfKin: "nextOfKin",
            };

         const response = await PerformRequest({
            method: Endpoints.GetUpdateKYCRequestStatus(userID).method,
            route: Endpoints.GetUpdateKYCRequestStatus(userID).route,
            data: {
               field: apiFieldMap[field],
               status,
            },
         });

         if (response && response.status === HttpCodes.Success) {
            success(`KYC document ${status === "approved" ? "approved" : "rejected"} successfully!`);
            // Reload KYC data to sync with server (this will update the list)
            await reloadKYC();
         } else {
            error(response?.message || `Failed to update ${field} verification`);
            // Reload to get correct state from server
            await reloadKYC();
         }
      } catch (err: any) {
         error(err.message || `Failed to update ${field} verification`);
         // Reload to get correct state from server
         await reloadKYC();
      } finally {
         setUpdatingField(null);
      }
   };

   const handleVerifyKYC = async (userID: string) => {
      setUpdatingKycId(userID);
      try {
         const response = await PerformRequest({
            method: Endpoints.GetUpdateKYCRequestStatus(userID).method,
            route: Endpoints.GetUpdateKYCRequestStatus(userID).route,
         });

         if (response && response.status === HttpCodes.Success) {
            success("KYC verified successfully!");
            await reloadKYC();
         } else {
            error(response?.message || "Failed to verify KYC");
         }
      } catch (err: any) {
         error(err.message || "Failed to verify KYC");
      } finally {
         setUpdatingKycId(null);
      }
   };

   const confirmAction = async () => {
      if (!confirmation) return;
      await handleUpdateFieldVerification(confirmation.userID, confirmation.field, confirmation.status);
      setConfirmation(null);
   };

   // Filter data based on active tab
   const filteredData =
      kycResponse?.data?.filter((kyc) => {
         if (activeTab === "all") return true;
         const status = getKycStatus(kyc);
         return status === activeTab;
      }) || [];

   return (
      <Container size="xl" py="xl">
         <Stack gap="xl">
            {/* Header Section */}
            <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
               <Group justify="space-between" align="flex-end" mb="md">
                  <div>
                     <Title order={1} size="3rem" fw={800} style={{ color: "#679369", marginBottom: "0.5rem" }}>
                        KYC Management
                     </Title>
                     <Text size="lg" c="dimmed">
                        Review and manage user KYC submissions
                     </Text>
                  </div>
               </Group>

               {/* Stats Cards */}
               <Group grow mb="md">
                  <Paper
                     p="md"
                     radius="sm"
                     style={{
                        background: "linear-gradient(135deg, #E8F5E9 0%, #C8E6C9 100%)",
                        border: "1px solid #699269",
                     }}
                  >
                     <Group gap="xs">
                        <ThemeIcon size="lg" radius="sm" style={{ background: "#699269" }}>
                           <IconFileCheck size={20} />
                        </ThemeIcon>
                        <div>
                           <Text size="xs" fw={500} c="#558B2F">
                              Total Submissions
                           </Text>
                           <Text size="xl" fw={700} c="#699269">
                              {stats.total}
                           </Text>
                        </div>
                     </Group>
                  </Paper>

                  <Paper
                     p="md"
                     radius="sm"
                     style={{
                        background: "linear-gradient(135deg, #EEF2FF 0%, #E0E7FF 100%)",
                        border: "1px solid #6366F1",
                     }}
                  >
                     <Group gap="xs">
                        <ThemeIcon size="lg" radius="sm" style={{ background: "#6366F1" }}>
                           <IconClock size={20} />
                        </ThemeIcon>
                        <div>
                           <Text size="xs" fw={500} c="#312E81">
                              Partial / Pending
                           </Text>
                           <Text size="xl" fw={700} c="#6366F1">
                              {stats.partial}
                           </Text>
                        </div>
                     </Group>
                  </Paper>

                  <Paper
                     p="md"
                     radius="sm"
                     style={{
                        background: "linear-gradient(135deg, #DCFCE7 0%, #BBF7D0 100%)",
                        border: "1px solid #22C55E",
                     }}
                  >
                     <Group gap="xs">
                        <ThemeIcon size="lg" radius="sm" style={{ background: "#22C55E" }}>
                           <IconCheck size={20} />
                        </ThemeIcon>
                        <div>
                           <Text size="xs" fw={500} c="#166534">
                              Verified
                           </Text>
                           <Text size="xl" fw={700} c="#22C55E">
                              {stats.verified}
                           </Text>
                        </div>
                     </Group>
                  </Paper>
               </Group>

               {/* Search Bar */}
               <TextInput
                  placeholder="Search by email or name..."
                  leftSection={<IconSearch size={18} />}
                  size="md"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{ maxWidth: "400px" }}
               />
            </motion.div>

            {/* Tabs for filtering */}
            <Tabs value={activeTab} onChange={setActiveTab} color="green">
               <Tabs.List>
                  <Tabs.Tab value="all">All Submissions</Tabs.Tab>
                  <Tabs.Tab value="verified" leftSection={<IconCheck size={16} />}>
                     Verified
                  </Tabs.Tab>
                  <Tabs.Tab value="partial" leftSection={<IconClock size={16} />}>
                     Partial
                  </Tabs.Tab>
                  <Tabs.Tab value="pending" leftSection={<IconX size={16} />}>
                     Pending
                  </Tabs.Tab>
               </Tabs.List>
            </Tabs>

            {/* Loading State */}
            {isLoading ? (
               <Paper
                  shadow="sm"
                  p="xl"
                  radius="sm"
                  style={{
                     background: "white",
                     border: "2px solid #e5e7eb",
                  }}
               >
                  <Stack align="center" gap="lg" py="xl">
                     <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#699269]"></div>

                     <Text size="lg" c="dimmed">
                        Loading KYC submissions...
                     </Text>
                  </Stack>
               </Paper>
            ) : !filteredData || filteredData.length === 0 ? (
               /* Empty State */
               <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.4 }}
               >
                  <Paper
                     shadow="md"
                     p="xl"
                     radius="sm"
                     style={{
                        background: "linear-gradient(135deg, #f9fafb 0%, #E8F5E9 100%)",
                        border: "2px solid #e5e7eb",
                     }}
                  >
                     <Stack align="center" gap="xl" py="xl">
                        <motion.div
                           initial={{ scale: 0 }}
                           animate={{ scale: 1 }}
                           transition={{ delay: 0.2, type: "spring" }}
                        >
                           <ThemeIcon
                              size={120}
                              radius="md"
                              style={{
                                 background: "linear-gradient(135deg, #E8F5E9 0%, #C8E6C9 100%)",
                              }}
                           >
                              <IconFileCheck size={60} style={{ color: "#699269" }} />
                           </ThemeIcon>
                        </motion.div>
                        <div style={{ textAlign: "center" }}>
                           <Title order={2} fw={700} mb="sm" style={{ color: "#111827" }}>
                              No KYC submissions found
                           </Title>
                           <Text size="lg" c="dimmed" style={{ maxWidth: "500px" }}>
                              There are no KYC submissions matching your filters.
                           </Text>
                        </div>
                     </Stack>
                  </Paper>
               </motion.div>
            ) : (
               /* KYC Table */
               <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
               >
                  <Paper
                     shadow="sm"
                     radius="sm"
                     style={{
                        background: "white",
                        border: "2px solid #e5e7eb",
                        overflow: "auto",
                     }}
                  >
                     <Table striped highlightOnHover withTableBorder withColumnBorders>
                        <Table.Thead>
                           <Table.Tr style={{ background: "#F9FAFB" }}>
                              <Table.Th style={{ fontWeight: 700, color: "#679369", width: "25%" }}>User</Table.Th>
                              <Table.Th style={{ fontWeight: 700, color: "#679369", width: "15%" }}>Status</Table.Th>
                              <Table.Th
                                 style={{ fontWeight: 700, color: "#679369", width: "10%", textAlign: "center" }}
                              >
                                 Identity
                              </Table.Th>
                              <Table.Th
                                 style={{ fontWeight: 700, color: "#679369", width: "10%", textAlign: "center" }}
                              >
                                 Address
                              </Table.Th>
                              <Table.Th
                                 style={{ fontWeight: 700, color: "#679369", width: "10%", textAlign: "center" }}
                              >
                                 Employment
                              </Table.Th>
                              <Table.Th
                                 style={{ fontWeight: 700, color: "#679369", width: "10%", textAlign: "center" }}
                              >
                                 Financial
                              </Table.Th>
                              <Table.Th
                                 style={{ fontWeight: 700, color: "#679369", textAlign: "center", width: "20%" }}
                              >
                                 Actions
                              </Table.Th>
                           </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                           {filteredData.map((kyc) => {
                              const status = getKycStatus(kyc);
                              const StatusIcon = getStatusIcon(status);
                              return (
                                 <Table.Tr key={kyc?.id}>
                                    <Table.Td>
                                       <Text size="sm">{kyc?.userID?.email}</Text>
                                    </Table.Td>
                                    <Table.Td>
                                       <Badge size="md" radius="sm" color={getStatusColor(status)} tt="capitalize">
                                          {status}
                                       </Badge>
                                    </Table.Td>
                                    <Table.Td>
                                       <Group justify="center" gap="xs">
                                          {(() => {
                                             const status = getDocumentStatus(kyc, "identity");
                                             const statusInfo = getDocumentStatusIcon(status);
                                             const StatusIcon = statusInfo.icon;
                                             return (
                                                <Tooltip label={statusInfo.label}>
                                                   <ThemeIcon color={statusInfo.color} size="md" radius="sm">
                                                      <StatusIcon size={16} />
                                                   </ThemeIcon>
                                                </Tooltip>
                                             );
                                          })()}
                                       </Group>
                                    </Table.Td>
                                    <Table.Td>
                                       <Group justify="center" gap="xs">
                                          {(() => {
                                             const status = getDocumentStatus(kyc, "address");
                                             const statusInfo = getDocumentStatusIcon(status);
                                             const StatusIcon = statusInfo.icon;
                                             return (
                                                <Tooltip label={statusInfo.label}>
                                                   <ThemeIcon color={statusInfo.color} size="md" radius="sm">
                                                      <StatusIcon size={16} />
                                                   </ThemeIcon>
                                                </Tooltip>
                                             );
                                          })()}
                                       </Group>
                                    </Table.Td>
                                    <Table.Td>
                                       <Group justify="center" gap="xs">
                                          {(() => {
                                             const status = getDocumentStatus(kyc, "employment");
                                             const statusInfo = getDocumentStatusIcon(status);
                                             const StatusIcon = statusInfo.icon;
                                             return (
                                                <Tooltip label={statusInfo.label}>
                                                   <ThemeIcon color={statusInfo.color} size="md" radius="sm">
                                                      <StatusIcon size={16} />
                                                   </ThemeIcon>
                                                </Tooltip>
                                             );
                                          })()}
                                       </Group>
                                    </Table.Td>
                                    <Table.Td>
                                       <Group justify="center" gap="xs">
                                          {(() => {
                                             const status = getDocumentStatus(kyc, "financial");
                                             const statusInfo = getDocumentStatusIcon(status);
                                             const StatusIcon = statusInfo.icon;
                                             return (
                                                <Tooltip label={statusInfo.label}>
                                                   <ThemeIcon color={statusInfo.color} size="md" radius="sm">
                                                      <StatusIcon size={16} />
                                                   </ThemeIcon>
                                                </Tooltip>
                                             );
                                          })()}
                                       </Group>
                                    </Table.Td>

                                    <Table.Td>
                                       <Group justify="center" gap="xs">
                                          <Menu shadow="md" width={200}>
                                             <Menu.Target>
                                                <ActionIcon
                                                   variant="subtle"
                                                   color="gray"
                                                   disabled={updatingKycId === kyc.userID.id}
                                                >
                                                   <IconDots size={20} />
                                                </ActionIcon>
                                             </Menu.Target>

                                             <Menu.Dropdown>
                                                <Menu.Item
                                                   leftSection={<IconEye size={16} />}
                                                   onClick={() => handleViewDetails(kyc)}
                                                >
                                                   View Details
                                                </Menu.Item>
                                             </Menu.Dropdown>
                                          </Menu>
                                       </Group>
                                    </Table.Td>
                                 </Table.Tr>
                              );
                           })}
                        </Table.Tbody>
                     </Table>
                  </Paper>
               </motion.div>
            )}

            {/* Pagination */}
            {filteredData && filteredData.length > 0 && totalPages > 1 && (
               <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}>
                  <Paper
                     p="lg"
                     radius="sm"
                     style={{
                        background: "white",
                        border: "2px solid #e5e7eb",
                     }}
                  >
                     <Group justify="center">
                        <Pagination
                           total={totalPages}
                           value={pagination.page}
                           onChange={(page) => setPagination({ ...pagination, page })}
                           size="lg"
                           radius="sm"
                           color="green"
                           styles={{
                              control: {
                                 border: "2px solid #e5e7eb",
                                 "&[data-active]": {
                                    background: "linear-gradient(135deg, #699269 0%, #81C784 100%)",
                                    border: "2px solid #699269",
                                    fontWeight: 700,
                                 },
                                 "&:hover": {
                                    background: "#E8F5E9",
                                 },
                              },
                           }}
                        />
                     </Group>
                     <Text size="sm" c="dimmed" ta="center" mt="md">
                        Showing page {pagination.page} of {totalPages} • {kycResponse.totalRecords || 0} total
                        submissions
                     </Text>
                  </Paper>
               </motion.div>
            )}
         </Stack>

         {/* KYC Details Modal */}
         <Modal
            opened={modalOpened}
            onClose={() => setModalOpened(false)}
            title={
               <Group gap="sm">
                  <ThemeIcon
                     size="lg"
                     radius="md"
                     style={{ background: "linear-gradient(135deg, #699269 0%, #81C784 100%)" }}
                  >
                     <IconShieldCheck size={24} />
                  </ThemeIcon>
                  <div>
                     <Text size="xl" fw={700} style={{ color: "#679369" }}>
                        KYC Verification Details
                     </Text>
                     <Text size="sm" c="dimmed">
                        Review and verify user documents
                     </Text>
                  </div>
               </Group>
            }
            size="xl"
            padding="xl"
            centered
            styles={{
               content: {
                  background: "linear-gradient(135deg, #ffffff 0%, #f9fafb 100%)",
               },
               header: {
                  borderBottom: "2px solid #e5e7eb",
                  paddingBottom: "1rem",
               },
            }}
         >
            <br />
            {selectedKyc && (
               <Stack gap="xl">
                  {/* Status Badge */}
                  <motion.div
                     initial={{ opacity: 0, y: -10 }}
                     animate={{ opacity: 1, y: 0 }}
                     transition={{ duration: 0.3 }}
                  >
                     <Paper
                        p="md"
                        radius="md"
                        style={{
                           background:
                              getKycStatus(selectedKyc) === "verified"
                                 ? "linear-gradient(135deg, #DCFCE7 0%, #BBF7D0 100%)"
                                 : "linear-gradient(135deg, #FFF9E6 0%, #FFF3CC 100%)",
                           border: getKycStatus(selectedKyc) === "verified" ? "2px solid #22C55E" : "2px solid #F59E0B",
                        }}
                     >
                        <Group justify="space-between">
                           <Group gap="sm">
                              <ThemeIcon
                                 size="xl"
                                 radius="md"
                                 style={{
                                    background: getKycStatus(selectedKyc) === "verified" ? "#22C55E" : "#F59E0B",
                                 }}
                              >
                                 {getKycStatus(selectedKyc) === "verified" ? (
                                    <IconCheck size={28} />
                                 ) : (
                                    <IconClock size={28} />
                                 )}
                              </ThemeIcon>
                              <div>
                                 <Text size="xs" tt="uppercase" fw={600} c="dimmed">
                                    Overall Status
                                 </Text>
                                 <Badge
                                    size="lg"
                                    radius="sm"
                                    color={getKycStatus(selectedKyc) === "verified" ? "green" : "yellow"}
                                    tt="uppercase"
                                    fw={700}
                                 >
                                    {getKycStatus(selectedKyc) === "verified" ? "Verified" : "Pending Verification"}
                                 </Badge>
                              </div>
                           </Group>
                           <div style={{ textAlign: "right" }}>
                              <Text size="xs" c="dimmed">
                                 Submitted on
                              </Text>
                              <Text size="sm" fw={600}>
                                 {formatDate(selectedKyc.createdAt)}
                              </Text>
                           </div>
                        </Group>
                     </Paper>
                  </motion.div>

                  <Divider label="User Information" labelPosition="center" />

                  {/* User Details */}
                  <motion.div
                     initial={{ opacity: 0, y: 10 }}
                     animate={{ opacity: 1, y: 0 }}
                     transition={{ duration: 0.3, delay: 0.1 }}
                  >
                     <Paper
                        p="lg"
                        radius="md"
                        style={{
                           background: "white",
                           border: "2px solid #e5e7eb",
                        }}
                     >
                        <Grid gutter="lg">
                           <Grid.Col span={{ base: 12, sm: 4 }}>
                              <Group gap="sm">
                                 <ThemeIcon size="lg" radius="md" color="blue" variant="light">
                                    <IconMail size={20} />
                                 </ThemeIcon>
                                 <div>
                                    <Text size="xs" c="dimmed" fw={500}>
                                       Email Address
                                    </Text>
                                    <Text size="sm" fw={600}>
                                       {selectedKyc?.userID?.email}
                                    </Text>
                                 </div>
                              </Group>
                           </Grid.Col>
                           <Grid.Col span={{ base: 12, sm: 4 }}>
                              <Group gap="sm">
                                 <ThemeIcon size="lg" radius="md" color="teal" variant="light">
                                    <IconPhone size={20} />
                                 </ThemeIcon>
                                 <div>
                                    <Text size="xs" c="dimmed" fw={500}>
                                       Phone Number
                                    </Text>
                                    <Text size="sm" fw={600}>
                                       {selectedKyc.userID.phone}
                                    </Text>
                                 </div>
                              </Group>
                           </Grid.Col>
                           <Grid.Col span={{ base: 12, sm: 4 }}>
                              <Group gap="sm">
                                 <ThemeIcon size="lg" radius="md" color="violet" variant="light">
                                    <IconUser size={20} />
                                 </ThemeIcon>
                                 <div>
                                    <Text size="xs" c="dimmed" fw={500}>
                                       User ID
                                    </Text>
                                    <Text size="sm" fw={600}>
                                       {selectedKyc.userID.id}
                                    </Text>
                                 </div>
                              </Group>
                           </Grid.Col>
                        </Grid>
                     </Paper>
                  </motion.div>

                  <Divider label="Verification Documents" labelPosition="center" />

                  {/* Identity Document */}
                  <motion.div
                     initial={{ opacity: 0, y: 10 }}
                     animate={{ opacity: 1, y: 0 }}
                     transition={{ duration: 0.3, delay: 0.2 }}
                  >
                     <Paper
                        p="lg"
                        radius="md"
                        style={{
                           background: "white",
                           border: `2px solid ${(() => {
                              const status = getDocumentStatus(selectedKyc, "identity");
                              if (status === "approved") return "#22C55E";
                              if (status === "pending") return "#F59E0B";
                              if (status === "rejected") return "#EF4444";
                              return "#EF4444";
                           })()}`,
                        }}
                     >
                        <Group justify="space-between" mb="md">
                           <Group gap="sm">
                              <ThemeIcon size="lg" radius="md" color="blue" variant="light">
                                 <IconFileText size={24} />
                              </ThemeIcon>
                              <div>
                                 <Text size="lg" fw={700}>
                                    Identity Document
                                 </Text>
                                 <Text size="xs" c="dimmed">
                                    Verification of user identity
                                 </Text>
                              </div>
                           </Group>
                           <Group gap="xs">
                              {(() => {
                                 const status = getDocumentStatus(selectedKyc, "identity");
                                 const isPending = status === "pending" || status === "awaiting";
                                 const isApproved = status === "approved";
                                 const isRejected = status === "rejected";

                                 if (isApproved || isRejected) {
                                    const statusInfo = getDocumentStatusIcon(status);
                                    return (
                                       <Badge
                                          size="lg"
                                          radius="sm"
                                          color={statusInfo.color}
                                          variant="filled"
                                          leftSection={<statusInfo.icon size={16} />}
                                       >
                                          {statusInfo.label}
                                       </Badge>
                                    );
                                 }

                                 return (
                                    <>
                                       <Button
                                          size="sm"
                                          color="green"
                                          variant="outline"
                                          onClick={() =>
                                             setConfirmation({
                                                userID: selectedKyc.userID.id,
                                                field: "identity",
                                                status: "approved",
                                             })
                                          }
                                          disabled={
                                             !hasDocument(selectedKyc, "identity") || updatingField === "identity"
                                          }
                                          leftSection={<IconCheck size={16} />}
                                       >
                                          Approve
                                       </Button>
                                       <Button
                                          size="sm"
                                          color="red"
                                          variant="outline"
                                          onClick={() =>
                                             setConfirmation({
                                                userID: selectedKyc.userID.id,
                                                field: "identity",
                                                status: "rejected",
                                             })
                                          }
                                          disabled={
                                             !hasDocument(selectedKyc, "identity") || updatingField === "identity"
                                          }
                                          leftSection={<IconX size={16} />}
                                       >
                                          Reject
                                       </Button>
                                    </>
                                 );
                              })()}
                           </Group>
                        </Group>

                        {selectedKyc.identity ? (
                           <Grid gutter="md">
                              <Grid.Col span={{ base: 12, sm: 6 }}>
                                 <Box
                                    p="md"
                                    style={{
                                       background: "#f9fafb",
                                       borderRadius: "8px",
                                       border: "1px solid #e5e7eb",
                                    }}
                                 >
                                    <Text size="xs" c="dimmed" fw={500} mb="xs">
                                       Document Type
                                    </Text>
                                    <Text size="sm" fw={600} tt="uppercase">
                                       {selectedKyc.identity.documentType}
                                    </Text>
                                 </Box>
                              </Grid.Col>
                              {selectedKyc.identity.value && (
                                 <Grid.Col span={{ base: 12, sm: 6 }}>
                                    <Box
                                       p="md"
                                       style={{
                                          background: "#f9fafb",
                                          borderRadius: "8px",
                                          border: "1px solid #e5e7eb",
                                       }}
                                    >
                                       <Text size="xs" c="dimmed" fw={500} mb="xs">
                                          Document Number
                                       </Text>
                                       <Text size="sm" fw={600}>
                                          {selectedKyc.identity.value}
                                       </Text>
                                    </Box>
                                 </Grid.Col>
                              )}
                              <Grid.Col span={12}>
                                 <Box
                                    p="md"
                                    style={{
                                       background: "#f9fafb",
                                       borderRadius: "8px",
                                       border: "1px solid #e5e7eb",
                                    }}
                                 >
                                    <Group justify="space-between" mb="sm">
                                       <Text size="xs" c="dimmed" fw={500}>
                                          Document File
                                       </Text>
                                       <Button
                                          size="xs"
                                          variant="light"
                                          leftSection={<IconDownload size={16} />}
                                          component="a"
                                          href={selectedKyc.identity.file}
                                          target="_blank"
                                          download
                                       >
                                          Download
                                       </Button>
                                    </Group>
                                    <Image
                                       src={selectedKyc.identity.file}
                                       alt="Identity Document"
                                       radius="md"
                                       fit="contain"
                                       h={200}
                                       style={{ border: "1px solid #e5e7eb" }}
                                    />
                                 </Box>
                              </Grid.Col>
                           </Grid>
                        ) : (
                           <Text size="sm" c="dimmed" ta="center" py="xl">
                              No identity document submitted
                           </Text>
                        )}
                     </Paper>
                  </motion.div>

                  {/* Proof of Address */}
                  <motion.div
                     initial={{ opacity: 0, y: 10 }}
                     animate={{ opacity: 1, y: 0 }}
                     transition={{ duration: 0.3, delay: 0.3 }}
                  >
                     <Paper
                        p="lg"
                        radius="md"
                        style={{
                           background: "white",
                           border: `2px solid ${(() => {
                              const status = getDocumentStatus(selectedKyc, "address");
                              if (status === "approved") return "#22C55E";
                              if (status === "pending") return "#F59E0B";
                              if (status === "rejected") return "#EF4444";
                              return "#EF4444";
                           })()}`,
                        }}
                     >
                        <Group justify="space-between" mb="md">
                           <Group gap="sm">
                              <ThemeIcon size="lg" radius="md" color="teal" variant="light">
                                 <IconHome size={24} />
                              </ThemeIcon>
                              <div>
                                 <Text size="lg" fw={700}>
                                    Proof of Address
                                 </Text>
                                 <Text size="xs" c="dimmed">
                                    Verification of residential address
                                 </Text>
                              </div>
                           </Group>
                           <Group gap="xs">
                              {(() => {
                                 const status = getDocumentStatus(selectedKyc, "address");
                                 const isPending = status === "pending" || status === "awaiting";
                                 const isApproved = status === "approved";
                                 const isRejected = status === "rejected";

                                 if (isApproved || isRejected) {
                                    const statusInfo = getDocumentStatusIcon(status);
                                    return (
                                       <Badge
                                          size="lg"
                                          radius="sm"
                                          color={statusInfo.color}
                                          variant="filled"
                                          leftSection={<statusInfo.icon size={16} />}
                                       >
                                          {statusInfo.label}
                                       </Badge>
                                    );
                                 }

                                 return (
                                    <>
                                       <Button
                                          size="sm"
                                          color="green"
                                          variant="outline"
                                          onClick={() =>
                                             setConfirmation({
                                                userID: selectedKyc.userID.id,
                                                field: "address",
                                                status: "approved",
                                             })
                                          }
                                          disabled={!hasDocument(selectedKyc, "address") || updatingField === "address"}
                                          leftSection={<IconCheck size={16} />}
                                       >
                                          Approve
                                       </Button>
                                       <Button
                                          size="sm"
                                          color="red"
                                          variant="outline"
                                          onClick={() =>
                                             setConfirmation({
                                                userID: selectedKyc.userID.id,
                                                field: "address",
                                                status: "rejected",
                                             })
                                          }
                                          disabled={!hasDocument(selectedKyc, "address") || updatingField === "address"}
                                          leftSection={<IconX size={16} />}
                                       >
                                          Reject
                                       </Button>
                                    </>
                                 );
                              })()}
                           </Group>
                        </Group>

                        {selectedKyc.proofOfAddress ? (
                           <Grid gutter="md">
                              <Grid.Col span={{ base: 12, sm: 6 }}>
                                 <Box
                                    p="md"
                                    style={{
                                       background: "#f9fafb",
                                       borderRadius: "8px",
                                       border: "1px solid #e5e7eb",
                                    }}
                                 >
                                    <Text size="xs" c="dimmed" fw={500} mb="xs">
                                       Document Type
                                    </Text>
                                    <Text size="sm" fw={600} tt="capitalize">
                                       {selectedKyc.proofOfAddress.documentType.replace(/_/g, " ")}
                                    </Text>
                                 </Box>
                              </Grid.Col>
                              <Grid.Col span={12}>
                                 <Box
                                    p="md"
                                    style={{
                                       background: "#f9fafb",
                                       borderRadius: "8px",
                                       border: "1px solid #e5e7eb",
                                    }}
                                 >
                                    <Group justify="space-between" mb="sm">
                                       <Text size="xs" c="dimmed" fw={500}>
                                          Document File
                                       </Text>
                                       <Button
                                          size="xs"
                                          variant="light"
                                          leftSection={<IconDownload size={16} />}
                                          component="a"
                                          href={selectedKyc.proofOfAddress.file}
                                          target="_blank"
                                          download
                                       >
                                          Download
                                       </Button>
                                    </Group>
                                    <Image
                                       src={selectedKyc.proofOfAddress.file}
                                       alt="Proof of Address"
                                       radius="md"
                                       fit="contain"
                                       h={200}
                                       style={{ border: "1px solid #e5e7eb" }}
                                    />
                                 </Box>
                              </Grid.Col>
                           </Grid>
                        ) : (
                           <Text size="sm" c="dimmed" ta="center" py="xl">
                              No proof of address submitted
                           </Text>
                        )}
                     </Paper>
                  </motion.div>

                  {/* Address Details */}
                  <motion.div
                     initial={{ opacity: 0, y: 10 }}
                     animate={{ opacity: 1, y: 0 }}
                     transition={{ duration: 0.3, delay: 0.35 }}
                  >
                     <Paper
                        p="lg"
                        radius="md"
                        style={{
                           background: "white",
                           border: "2px solid #e5e7eb",
                        }}
                     >
                        <Group justify="space-between" mb="md">
                           <Group gap="sm">
                              <ThemeIcon size="lg" radius="md" color="green" variant="light">
                                 <IconHome size={24} />
                              </ThemeIcon>
                              <div>
                                 <Text size="lg" fw={700}>
                                    Residential Address Details
                                 </Text>
                                 <Text size="xs" c="dimmed">
                                    Information provided by the user
                                 </Text>
                              </div>
                           </Group>
                        </Group>
                        {selectedKyc.addressInfo ? (
                           <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
                              {[
                                 { label: "Address", value: selectedKyc.addressInfo.addressLine },
                                 {
                                    label: "Nearest Landmark & Bus Stop",
                                    value: selectedKyc.addressInfo.nearestLandmarkAndBusStop,
                                 },
                                 { label: "City", value: selectedKyc.addressInfo.city },
                                 { label: "Area", value: selectedKyc.addressInfo.area },
                                 { label: "State", value: selectedKyc.addressInfo.state },
                                 { label: "Local Government Area", value: selectedKyc.addressInfo.localGovernmentArea },
                                 { label: "Country", value: selectedKyc.addressInfo.country },
                              ].map((item) => (
                                 <Box
                                    key={item.label}
                                    p="md"
                                    style={{
                                       background: "#f9fafb",
                                       borderRadius: "8px",
                                       border: "1px solid #e5e7eb",
                                    }}
                                 >
                                    <Text size="xs" c="dimmed" fw={500} mb="xs">
                                       {item.label}
                                    </Text>
                                    <Text size="sm" fw={600}>
                                       {item.value || "—"}
                                    </Text>
                                 </Box>
                              ))}
                           </SimpleGrid>
                        ) : (
                           <Text size="sm" c="dimmed" ta="center" py="sm">
                              No residential address information submitted.
                           </Text>
                        )}
                     </Paper>
                  </motion.div>

                  {/* Next of Kin */}
                  <motion.div
                     initial={{ opacity: 0, y: 10 }}
                     animate={{ opacity: 1, y: 0 }}
                     transition={{ duration: 0.3, delay: 0.4 }}
                  >
                     <Paper
                        p="lg"
                        radius="md"
                        style={{
                           background: "white",
                           border: `2px solid ${(() => {
                              const status = getDocumentStatus(selectedKyc, "nextOfKin");
                              if (status === "approved") return "#22C55E";
                              if (status === "pending") return "#F59E0B";
                              if (status === "rejected") return "#EF4444";
                              return "#EF4444";
                           })()}`,
                        }}
                     >
                        <Group justify="space-between" mb="md">
                           <Group gap="sm">
                              <ThemeIcon size="lg" radius="md" color="orange" variant="light">
                                 <IconUser size={24} />
                              </ThemeIcon>
                              <div>
                                 <Text size="lg" fw={700}>
                                    Next of Kin Information
                                 </Text>
                                 <Text size="xs" c="dimmed">
                                    Emergency contact details provided
                                 </Text>
                              </div>
                           </Group>
                           <Group gap="xs">
                              {(() => {
                                 const status = getDocumentStatus(selectedKyc, "nextOfKin");
                                 const isPending = status === "pending" || status === "awaiting";
                                 const isApproved = status === "approved";
                                 const isRejected = status === "rejected";

                                 if (isApproved || isRejected) {
                                    const statusInfo = getDocumentStatusIcon(status);
                                    return (
                                       <Badge
                                          size="lg"
                                          radius="sm"
                                          color={statusInfo.color}
                                          variant="filled"
                                          leftSection={<statusInfo.icon size={16} />}
                                       >
                                          {statusInfo.label}
                                       </Badge>
                                    );
                                 }

                                 return (
                                    <>
                                       <Button
                                          size="sm"
                                          color="green"
                                          variant="outline"
                                          onClick={() =>
                                             setConfirmation({
                                                userID: selectedKyc.userID.id,
                                                field: "nextOfKin",
                                                status: "approved",
                                             })
                                          }
                                          disabled={
                                             !hasDocument(selectedKyc, "nextOfKin") || updatingField === "nextOfKin"
                                          }
                                          leftSection={<IconCheck size={16} />}
                                       >
                                          Approve
                                       </Button>
                                       <Button
                                          size="sm"
                                          color="red"
                                          variant="outline"
                                          onClick={() =>
                                             setConfirmation({
                                                userID: selectedKyc.userID.id,
                                                field: "nextOfKin",
                                                status: "rejected",
                                             })
                                          }
                                          disabled={
                                             !hasDocument(selectedKyc, "nextOfKin") || updatingField === "nextOfKin"
                                          }
                                          leftSection={<IconX size={16} />}
                                       >
                                          Reject
                                       </Button>
                                    </>
                                 );
                              })()}
                           </Group>
                        </Group>
                        {selectedKyc.nextOfKin ? (
                           <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
                              {[
                                 {
                                    label: "Full Name",
                                    value: `${selectedKyc.nextOfKin.firstName} ${selectedKyc.nextOfKin.surname}`.trim(),
                                 },
                                 {
                                    label: "Relationship",
                                    value: selectedKyc.nextOfKin.relationship
                                       ? selectedKyc.nextOfKin.relationship.replace(/_/g, " ")
                                       : "—",
                                 },
                                 { label: "Phone Number", value: selectedKyc.nextOfKin.phoneNumber },
                                 { label: "Email Address", value: selectedKyc.nextOfKin.emailAddress },
                                 { label: "Address", value: selectedKyc.nextOfKin.address },
                                 {
                                    label: "Nearest Landmark & Bus Stop",
                                    value: selectedKyc.nextOfKin.nearestLandmarkAndBusStop,
                                 },
                                 { label: "City", value: selectedKyc.nextOfKin.city },
                                 { label: "State", value: selectedKyc.nextOfKin.state },
                                 { label: "Local Government Area", value: selectedKyc.nextOfKin.localGovernmentArea },
                                 { label: "Country", value: selectedKyc.nextOfKin.country },
                              ].map((item) => (
                                 <Box
                                    key={item.label}
                                    p="md"
                                    style={{
                                       background: "#f9fafb",
                                       borderRadius: "8px",
                                       border: "1px solid #e5e7eb",
                                    }}
                                 >
                                    <Text size="xs" c="dimmed" fw={500} mb="xs">
                                       {item.label}
                                    </Text>
                                    <Text size="sm" fw={600}>
                                       {item.value || "—"}
                                    </Text>
                                 </Box>
                              ))}
                           </SimpleGrid>
                        ) : (
                           <Text size="sm" c="dimmed" ta="center" py="sm">
                              No next of kin information submitted.
                           </Text>
                        )}
                     </Paper>
                  </motion.div>

                  {/* Proof of Employment */}
                  <motion.div
                     initial={{ opacity: 0, y: 10 }}
                     animate={{ opacity: 1, y: 0 }}
                     transition={{ duration: 0.3, delay: 0.4 }}
                  >
                     <Paper
                        p="lg"
                        radius="md"
                        style={{
                           background: "white",
                           border: `2px solid ${(() => {
                              const status = getDocumentStatus(selectedKyc, "employment");
                              if (status === "approved") return "#22C55E";
                              if (status === "pending") return "#F59E0B";
                              if (status === "rejected") return "#EF4444";
                              return "#EF4444";
                           })()}`,
                        }}
                     >
                        <Group justify="space-between" mb="md">
                           <Group gap="sm">
                              <ThemeIcon size="lg" radius="md" color="violet" variant="light">
                                 <IconBriefcase size={24} />
                              </ThemeIcon>
                              <div>
                                 <Text size="lg" fw={700}>
                                    Proof of Employment
                                 </Text>
                                 <Text size="xs" c="dimmed">
                                    Verification of employment status
                                 </Text>
                              </div>
                           </Group>
                           <Group gap="xs">
                              {(() => {
                                 const status = getDocumentStatus(selectedKyc, "employment");
                                 const isPending = status === "pending" || status === "awaiting";
                                 const isApproved = status === "approved";
                                 const isRejected = status === "rejected";

                                 if (isApproved || isRejected) {
                                    const statusInfo = getDocumentStatusIcon(status);
                                    return (
                                       <Badge
                                          size="lg"
                                          radius="sm"
                                          color={statusInfo.color}
                                          variant="filled"
                                          leftSection={<statusInfo.icon size={16} />}
                                       >
                                          {statusInfo.label}
                                       </Badge>
                                    );
                                 }

                                 return (
                                    <>
                                       <Button
                                          size="sm"
                                          color="green"
                                          variant="outline"
                                          onClick={() =>
                                             setConfirmation({
                                                userID: selectedKyc.userID.id,
                                                field: "employment",
                                                status: "approved",
                                             })
                                          }
                                          disabled={
                                             !hasDocument(selectedKyc, "employment") || updatingField === "employment"
                                          }
                                          leftSection={<IconCheck size={16} />}
                                       >
                                          Approve
                                       </Button>
                                       <Button
                                          size="sm"
                                          color="red"
                                          variant="outline"
                                          onClick={() =>
                                             setConfirmation({
                                                userID: selectedKyc.userID.id,
                                                field: "employment",
                                                status: "rejected",
                                             })
                                          }
                                          disabled={
                                             !hasDocument(selectedKyc, "employment") || updatingField === "employment"
                                          }
                                          leftSection={<IconX size={16} />}
                                       >
                                          Reject
                                       </Button>
                                    </>
                                 );
                              })()}
                           </Group>
                        </Group>

                        {selectedKyc.proofOfEmployment ? (
                           <Grid gutter="md">
                              <Grid.Col span={{ base: 12, sm: 6 }}>
                                 <Box
                                    p="md"
                                    style={{
                                       background: "#f9fafb",
                                       borderRadius: "8px",
                                       border: "1px solid #e5e7eb",
                                    }}
                                 >
                                    <Text size="xs" c="dimmed" fw={500} mb="xs">
                                       Document Type
                                    </Text>
                                    <Text size="sm" fw={600} tt="capitalize">
                                       {selectedKyc.proofOfEmployment.documentType.replace(/_/g, " ")}
                                    </Text>
                                 </Box>
                              </Grid.Col>
                              <Grid.Col span={12}>
                                 <Box
                                    p="md"
                                    style={{
                                       background: "#f9fafb",
                                       borderRadius: "8px",
                                       border: "1px solid #e5e7eb",
                                    }}
                                 >
                                    <Group justify="space-between" mb="sm">
                                       <Text size="xs" c="dimmed" fw={500}>
                                          Document File
                                       </Text>
                                       <Button
                                          size="xs"
                                          variant="light"
                                          leftSection={<IconDownload size={16} />}
                                          component="a"
                                          href={selectedKyc.proofOfEmployment.file}
                                          target="_blank"
                                          download
                                       >
                                          Download
                                       </Button>
                                    </Group>
                                    <Image
                                       src={selectedKyc.proofOfEmployment.file}
                                       alt="Proof of Employment"
                                       radius="md"
                                       fit="contain"
                                       h={200}
                                       style={{ border: "1px solid #e5e7eb" }}
                                    />
                                 </Box>
                              </Grid.Col>
                           </Grid>
                        ) : (
                           <Text size="sm" c="dimmed" ta="center" py="xl">
                              No proof of employment submitted
                           </Text>
                        )}
                     </Paper>
                  </motion.div>

                  {/* Financial Document */}
                  <motion.div
                     initial={{ opacity: 0, y: 10 }}
                     animate={{ opacity: 1, y: 0 }}
                     transition={{ duration: 0.3, delay: 0.5 }}
                  >
                     <Paper
                        p="lg"
                        radius="md"
                        style={{
                           background: "white",
                           border: `2px solid ${(() => {
                              const status = getDocumentStatus(selectedKyc, "financial");
                              if (status === "approved") return "#22C55E";
                              if (status === "pending") return "#F59E0B";
                              if (status === "rejected") return "#EF4444";
                              return "#EF4444";
                           })()}`,
                        }}
                     >
                        <Group justify="space-between" mb="md">
                           <Group gap="sm">
                              <ThemeIcon size="lg" radius="md" color="orange" variant="light">
                                 <IconCurrencyDollar size={24} />
                              </ThemeIcon>
                              <div>
                                 <Text size="lg" fw={700}>
                                    Financial Document
                                 </Text>
                                 <Text size="xs" c="dimmed">
                                    Bank statement verification
                                 </Text>
                              </div>
                           </Group>
                           <Group gap="xs">
                              {(() => {
                                 const status = getDocumentStatus(selectedKyc, "financial");
                                 const isPending = status === "pending" || status === "awaiting";
                                 const isApproved = status === "approved";
                                 const isRejected = status === "rejected";

                                 if (isApproved || isRejected) {
                                    const statusInfo = getDocumentStatusIcon(status);
                                    return (
                                       <Badge
                                          size="lg"
                                          radius="sm"
                                          color={statusInfo.color}
                                          variant="filled"
                                          leftSection={<statusInfo.icon size={16} />}
                                       >
                                          {statusInfo.label}
                                       </Badge>
                                    );
                                 }

                                 return (
                                    <>
                                       <Button
                                          size="sm"
                                          color="green"
                                          variant="outline"
                                          onClick={() =>
                                             setConfirmation({
                                                userID: selectedKyc.userID.id,
                                                field: "financial",
                                                status: "approved",
                                             })
                                          }
                                          disabled={
                                             !hasDocument(selectedKyc, "financial") || updatingField === "financial"
                                          }
                                          leftSection={<IconCheck size={16} />}
                                       >
                                          Approve
                                       </Button>
                                       <Button
                                          size="sm"
                                          color="red"
                                          variant="outline"
                                          onClick={() =>
                                             setConfirmation({
                                                userID: selectedKyc.userID.id,
                                                field: "financial",
                                                status: "rejected",
                                             })
                                          }
                                          disabled={
                                             !hasDocument(selectedKyc, "financial") || updatingField === "financial"
                                          }
                                          leftSection={<IconX size={16} />}
                                       >
                                          Reject
                                       </Button>
                                    </>
                                 );
                              })()}
                           </Group>
                        </Group>

                        {selectedKyc.financialDocument ? (
                           <Grid gutter="md">
                              <Grid.Col span={{ base: 12, sm: 6 }}>
                                 <Box
                                    p="md"
                                    style={{
                                       background: "#f9fafb",
                                       borderRadius: "8px",
                                       border: "1px solid #e5e7eb",
                                    }}
                                 >
                                    <Text size="xs" c="dimmed" fw={500} mb="xs">
                                       Document Type
                                    </Text>
                                    <Text size="sm" fw={600} tt="capitalize">
                                       {selectedKyc.financialDocument.documentType.replace(/_/g, " ")}
                                    </Text>
                                 </Box>
                              </Grid.Col>
                              <Grid.Col span={12}>
                                 <Box
                                    p="md"
                                    style={{
                                       background: "#f9fafb",
                                       borderRadius: "8px",
                                       border: "1px solid #e5e7eb",
                                    }}
                                 >
                                    <Group justify="space-between" mb="sm">
                                       <Text size="xs" c="dimmed" fw={500}>
                                          Document File
                                       </Text>
                                       <Button
                                          size="xs"
                                          variant="light"
                                          leftSection={<IconDownload size={16} />}
                                          component="a"
                                          href={selectedKyc.financialDocument.file}
                                          target="_blank"
                                          download
                                       >
                                          Download
                                       </Button>
                                    </Group>
                                    <Image
                                       src={selectedKyc.financialDocument.file}
                                       alt="Financial Document"
                                       radius="md"
                                       fit="contain"
                                       h={200}
                                       style={{ border: "1px solid #e5e7eb" }}
                                    />
                                 </Box>
                              </Grid.Col>
                           </Grid>
                        ) : (
                           <Text size="sm" c="dimmed" ta="center" py="xl">
                              No financial document submitted
                           </Text>
                        )}
                     </Paper>
                  </motion.div>
               </Stack>
            )}
         </Modal>

         {/* Confirmation Modal */}
         <Modal
            opened={Boolean(confirmation)}
            onClose={() => {
               if (updatingField) return;
               setConfirmation(null);
            }}
            title={
               <Text fw={600} size="lg">
                  Confirm Your Decision
               </Text>
            }
            centered
            size="sm"
         >
            {confirmation && (
               <Stack gap="md">
                  <Text>
                     You are about to {confirmation.status === "approved" ? "approve" : "reject"} the{" "}
                     {DOCUMENT_LABELS[confirmation.field]}. This action cannot be reversed.
                  </Text>
                  <Text c="dimmed" size="sm">
                     This status can only change if the user uploads a new document (which will move the status back to
                     pending).
                  </Text>
                  <Group justify="flex-end" gap="sm">
                     <Button
                        variant="default"
                        onClick={() => {
                           if (updatingField) return;
                           setConfirmation(null);
                        }}
                        disabled={Boolean(updatingField)}
                     >
                        Cancel
                     </Button>
                     <Button
                        color={confirmation.status === "approved" ? "green" : "red"}
                        onClick={confirmAction}
                        loading={Boolean(updatingField)}
                     >
                        {confirmation.status === "approved" ? "Yes, Approve" : "Yes, Reject"}
                     </Button>
                  </Group>
               </Stack>
            )}
         </Modal>
      </Container>
   );
}
